import React from 'react';
import { Box, Grid } from '@mui/material';
import { SubFunctionAuxiliaryHint } from '../rbp-cloud-sub-function/sub-function-auxiliary-hint.component';
import Card from '../dish-menu-creator/Card';
import _ from 'lodash';
import GlobalNotification from '../../component/common/snackbar';
import i18n from '../../i18n';
import { FirstScreenMediaCardHeader } from './first-screen-media-card-header.component';
import { FirstScreenBundledContext, type FirstScreenBundledContextValue } from './first-screen-bundled-context';
import { getRBPStringId } from '../main/main-page-states';
import apiManage from '../../request';
import { BULLETIN_DINING_GUIDE_MAX_SLOTS, withImageCacheBust } from '../../utils/restaurant-static-bundled';
import { RestaurantStaticImageUpload } from '../../component/restaurant-static-image-upload.component';
import { tFirstScreenCardTitleWithSuggestedImageSize } from './first-screen-card-title-i18n';

/** 图片就餐指南图位总数，与 `BULLETIN_DINING_GUIDE_MAX_SLOTS`、稿面「已用/总数」一致 */
const BULLETIN_IMAGE_SLOT_TOTAL = BULLETIN_DINING_GUIDE_MAX_SLOTS;

/** 图片就餐指南：多图位上传，走 v1 `bulletin-dining-guide-image`；上传 query `number` 1–5；上传控件与菜单库一致并带 Figma 空态。 */
export default class BulletinImageComponent extends React.Component<Props> {
    /** 首页 bundled Context */
    public static contextType = FirstScreenBundledContext;

    /**
     * React 注入的 Context（避免 `declare context` 导致 CRA Babel 构建失败）。
     */
    private get bundledPageContext(): FirstScreenBundledContextValue | null {
        return (this as unknown as { context: FirstScreenBundledContextValue | null }).context;
    }

    /**
     * 更换图片就餐指南某一槽位。
     * @param file - 上传文件
     * @param slotIndex - 槽位序号 1..BULLETIN_IMAGE_SLOT_TOTAL
     */
    async onChangeAvatarUrl(file: File, slotIndex: number) {
        const stringId = getRBPStringId();
        const result = await apiManage.post_restaurant_static_bulletin_dining_guide_image(
            stringId,
            file,
            slotIndex,
        );
        if (!_.get(result, 'error')) {
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            await this.bundledPageContext?.reloadBundled();
        } else {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        }
    }

    /**
     * 删除指定槽位的就餐指南图。
     * @param slotIndex - 槽位序号 1..BULLETIN_IMAGE_SLOT_TOTAL
     */
    async deleteImage(slotIndex: number) {
        const stringId = getRBPStringId();
        const result = await apiManage.delete_restaurant_static_bulletin_dining_guide_image(stringId, slotIndex);
        if (!_.get(result, 'error')) {
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            await this.bundledPageContext?.reloadBundled();
        } else {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        }
    }

    /**
     * 渲染图片就餐指南卡片。
     */
    render() {
        /** 云 RBP 子功能顶栏「辅助模式」 */
        const auxiliaryModeEnabled = this.props.auxiliaryModeEnabled === true;
        const ctx = this.bundledPageContext;
        const bust = ctx?.bustVersion ?? 0;
        const urls = ctx?.bundled.bulletinUrls ?? [];
        const bulletinImageSlotUsed = urls
            .slice(0, BULLETIN_IMAGE_SLOT_TOTAL)
            .filter((u) => String(u || '').trim()).length;
        const stringId = getRBPStringId();

        return (
            <Card
                className="common-card"
                title={(
                    <Box sx={{ width: '100%' }}>
                        <FirstScreenMediaCardHeader
                            title={tFirstScreenCardTitleWithSuggestedImageSize(
                                'first_screen_setting_page_picture_meal_guide',
                            )}
                            usedCount={bulletinImageSlotUsed}
                            totalCount={BULLETIN_IMAGE_SLOT_TOTAL}
                        />
                        <SubFunctionAuxiliaryHint
                            enabled={auxiliaryModeEnabled}
                            i18nKey="first_screen_aux_picture_meal_guide"
                        />
                    </Box>
                )}
                contentRender={(
                    <Grid container spacing={3}>
                        {Array(BULLETIN_IMAGE_SLOT_TOTAL)
                            .fill(1)
                            .map((_item, index) => {
                                const slotIndex = index + 1;
                                const rawUrl = String(urls[index] ?? '').trim();
                                const displayUrl = withImageCacheBust(rawUrl, bust) ?? '';
                                return (
                                    <Grid item key={index}>
                                        <RestaurantStaticImageUpload
                                            stringId={stringId}
                                            kind="dish-image"
                                            uploadEntityId={1}
                                            deferStaticUpload
                                            imageRef={rawUrl}
                                            displayUrl={displayUrl}
                                            onStagedStaticFile={async (file) => {
                                                await this.onChangeAvatarUrl(file, slotIndex);
                                            }}
                                            onDeferredStaticRemove={async () => {
                                                await this.deleteImage(slotIndex);
                                            }}
                                            uploadEmptyVariant="firstScreenGallery"
                                            slotWidth={256}
                                            slotHeight={160}
                                            sx={{ minHeight: 160 }}
                                        />
                                    </Grid>
                                );
                            })}
                    </Grid>
                )}
            />
        );
    }
}

type Props = {
    /** 云 RBP 子功能壳顶栏「辅助模式」是否开启 */
    auxiliaryModeEnabled?: boolean;
};
